import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
    BatterySetDto,
    BatterySetService,
    FlyFreelyLoggingService,
    NameId,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    PersonsOrganisationDto,
    ShareResourceCommand,
    UnshareResourceCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

@Component({
    selector: 'shared-battery-sets',
    styleUrls: ['./common.scss'],
    template: `<h5 [ngClass]="{ readonly: working }">Shared Battery Sets</h5>
        <div class="input-group">
            <ng-select
                [items]="
                    organisationBatterySetList$
                        | async
                        | sortBy: 'name'
                        | filterOutById: relationship.sharedBatterySetList
                "
                bindValue="id"
                bindLabel="name"
                appendTo="body"
                [formControl]="addBatterySet"
                placeholder="Select battery set to share with {{
                    relationship.childOrganisation.name
                }}"
                [readonly]="working || isReadOnlyAdmin"
                [clearable]="false"
            >
                <ng-template ng-option-tmp let-item="item" let-index="index">
                    <battery-set-list-item
                        [item]="item"
                    ></battery-set-list-item>
                </ng-template>
                <ng-template ng-label-tmp let-item="item" let-index="index">
                    <status-icon
                        [batterySet]="item"
                        [clickable]="false"
                    ></status-icon>
                </ng-template>
            </ng-select>
            <span class="input-group-btn">
                <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="
                        working || !addBatterySet.valid || isReadOnlyAdmin
                    "
                    (click)="shareBatterySet()"
                >
                    Share
                </button>
            </span>
        </div>
        <div>
            <div
                *ngFor="
                    let batterySet of relationship.sharedBatterySetList
                        | sortBy: 'name'
                "
                class="top-buffer horizontal-container center-children"
            >
                <battery-set-list-item
                    class="shared-item"
                    [item]="batterySet"
                    [showCustodian]="true"
                ></battery-set-list-item>

                <button
                    *ngIf="!isReadOnlyAdmin"
                    type="button"
                    class="btn btn-tertiary btn-delete"
                    tooltip="Remove sharing of battery set"
                    placement="top"
                    (click)="unshareBatterySet(batterySet)"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>

                <div>
                    <button
                        *ngIf="batterySet.custodian == null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the custodian"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateCustodian(batterySet.id)"
                    >
                        Set as Custodian
                    </button>
                    <button
                        *ngIf="batterySet.custodian != null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set custodian back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="clearCustodian(batterySet.id)"
                    >
                        Clear Custodian
                    </button>
                    <button
                        *ngIf="
                            batterySet.maintenanceOrganisation.id !==
                            relationship.childOrganisation.id
                        "
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the maintenance organisation"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateMaintenanceOrganisation(batterySet.id)"
                    >
                        Set as Maintenance Organisation
                    </button>
                    <button
                        *ngIf="
                            batterySet.maintenanceOrganisation.id ===
                            relationship.childOrganisation.id
                        "
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set maintenance organisation back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="cleanMaintenanceOrganisation(batterySet)"
                    >
                        Clear as Maintenance Organisation
                    </button>
                </div>
            </div>
        </div>`
})
export class SharedBatterySets implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;
    @Input() relationship: OrganisationRelationshipDto;
    @Input() isReadOnlyAdmin = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    organisationBatterySetList$: Observable<NameId[]>;

    addBatterySet = new FormControl(undefined, [Validators.required]);

    constructor(
        public workTracker: WorkTracker,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationRelationshipsUi: OrganisationRelationshipsUiService,
        private batterySetService: BatterySetService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit() {
        this.organisationBatterySetList$ =
            this.organisationRelationshipsUi.organisationBatterySetList$.pipe(
                map(res =>
                    res.filter(
                        item => item.organisationId === this.organisation.id
                    )
                )
            );
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    shareBatterySet() {
        const cmd: ShareResourceCommand = {
            resourceId: this.addBatterySet.value
        };
        this.organisationRelationshipService
            .shareBatterySet(this.organisation.id, this.relationship.id, cmd)
            .subscribe(results => {
                this.addBatterySet.setValue(undefined);
                this.angulartics2.eventTrack.next({
                    action: 'share-battery-set',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    unshareBatterySet(batterySet: BatterySetDto) {
        const cmd: UnshareResourceCommand = { resourceId: batterySet.id };
        this.organisationRelationshipService
            .unshareBatterySet(this.organisation.id, this.relationship.id, cmd)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'unshare-battery-set',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    updateCustodian(batterySetId: number) {
        this.batterySetService
            .updateCustodian(batterySetId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Custodian set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating battery set custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    clearCustodian(batterySetId: number) {
        this.batterySetService
            .updateCustodian(batterySetId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: null
            })
            .subscribe({
                next: () => this.logging.success('Custodian set back to owner'),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating battery set custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateMaintenanceOrganisation(batterySetId: number) {
        this.batterySetService
            .updateMaintenanceOrganisation(batterySetId, {
                managingOrganisationId: this.organisation.id,
                maintenanceOrganisationId:
                    this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Maintenance organisation set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating battery set maintenance organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    cleanMaintenanceOrganisation(batterySet: BatterySetDto) {
        this.batterySetService
            .updateMaintenanceOrganisation(batterySet.id, {
                managingOrganisationId: this.organisation.id,
                maintenanceOrganisationId: batterySet.organisationId
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        'Maintenance organisation set back to owner'
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating battery set maintenance organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
